import React, {useContext, useRef, useState} from 'react';
import {Button, Input, Modal, Table, Tag} from "antd";
import dayjs from "dayjs";
import {PlusOutlined} from "@ant-design/icons";
import {usePersonalPackage, usePersonalPackageGroupAll, useSavePersonalPackage} from "./usePersonalPackage";
import {useItemKrTextData, useShopPackage, useShopPackageGroup} from "../../probability/useItemData";
import IsLoading from "../../../shared/components/IsLoading";
import ErrorPage from "../../../shared/components/ErrorPage";
import {AuthContext} from "../../../shared/contexts/authContext";
import {getItemName} from "../../../shared/utils/basic";
import PersonalPackageEditModal from "./PersonalPackageEditModal";
import PersonalPackageRegisterModal from "./PersonalPackageRegisterModal";
import {PersonalPackageEntity} from "./PersonalPackageEntity";
import toast from "react-hot-toast";
import {internalApi} from "../../../common/axios";
import {ENV, REFRESH_TYPE_FUNCTION} from "../../../shared/utils/enum";

const PersonalPackage = () => {
    const {currentUser} = useContext(AuthContext);

    const refreshRef = useRef(null);

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [registerModalOpen, setRegisterModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [editingMemo, setEditingMemo] = useState({});

    const {data: personalPackages, isLoading, error, refetch} = usePersonalPackage();
    const {data: allGroups, isLoading: isGroupLoading, error: groupError, refetch: refetchGroups} = usePersonalPackageGroupAll();
    const {data: shopPackage, isLoading: isShopLoading, error: shopError} = useShopPackage();
    const {data: shopPackageGroup, isLoading: isShopGroupLoading, error: shopGroupError} = useShopPackageGroup();
    const {data: krItemTextData, isLoading: isTextLoading, error: textError} = useItemKrTextData();
    const {mutate: saveMemo} = useSavePersonalPackage();

    const handleOpenEdit = (record) => {
        setSelectedRecord(record || new PersonalPackageEntity());
        setEditModalOpen(true);
    };

    const handleOpenRegister = (record) => {
        setSelectedRecord(record);
        setRegisterModalOpen(true);
    };

    const handleCloseModal = () => {
        setEditModalOpen(false);
        setRegisterModalOpen(false);
        refetch();
        refetchGroups();
    };

    const handleMemoSave = async (record, memo) => {
        try {
            await internalApi.post('store_manager/personalPackage', {data: {...record, memo}});
            toast.success('메모 저장 완료');
            refetch();
        } catch {
            toast.error('메모 저장 실패');
        }
        setEditingMemo({});
    };

    const getGroupCount = (seq) => (allGroups || []).filter(g => g.groupIdx === seq).length;

    // Level 2 expand: shop_package_group details per packageIndex
    const shopItemColumns = [
        {title: '상품 인덱스', dataIndex: 'index', key: 'index', width: 100},
        {title: '상품이름', dataIndex: 'nameText', key: 'nameText'},
        {title: '인앱 인덱스', dataIndex: 'inAppIdx', key: 'inAppIdx', width: 120},
        {title: '다이아 가격', dataIndex: 'diaBuyValue', key: 'diaBuyValue', width: 100},
        {
            title: '보상 아이템', dataIndex: 'rewards', key: 'rewards',
            render: (rewards) => (rewards || []).map((reward, idx) => (
                <div key={idx}>{getItemName(krItemTextData[currentUser.target], reward.id, reward.type)} x{reward.quantity}</div>
            )),
        },
    ];

    const groupExpandRender = (groupRecord) => {
        const pkgEntry = shopPackage[currentUser.target][groupRecord.packageIndex];
        if (!pkgEntry) return <div style={{padding: 8, color: '#999'}}>패키지 정보 없음 (인덱스: {groupRecord.packageIndex})</div>;

        const items = Object.values(shopPackageGroup[currentUser.target])
            .filter(g => g.groupIdx === pkgEntry.groupIdx)
            .map((g, i) => ({...g, key: i}));

        return (
            <Table
                columns={shopItemColumns}
                dataSource={items}
                pagination={false}
                size="small"
                style={{marginLeft: 24}}
            />
        );
    };

    // Level 1 expand: personal_package_group items
    const groupColumns = [
        {
            title: '평균 결제 금액(달러)',
            key: 'avrPayment',
            width: 200,
            render: (_, r) => (
                r.minAvrPayment === 0 && r.maxAvrPayment === 0
                    ? <Tag>제한 없음</Tag>
                    : <Tag color="blue">${r.minAvrPayment} ~ ${r.maxAvrPayment}</Tag>
            ),
        },
        {
            title: '레벨',
            key: 'level',
            width: 140,
            render: (_, r) => (
                r.minLevel === 0 && r.maxLevel === 0
                    ? <Tag>제한 없음</Tag>
                    : <Tag color="green">{r.minLevel} ~ {r.maxLevel}</Tag>
            ),
        },
        {title: '할인 타입', dataIndex: 'saleType', key: 'saleType', width: 100},
        {title: '패키지 인덱스', dataIndex: 'packageIndex', key: 'packageIndex', width: 120},
        {title: '패키지 이름', dataIndex: 'packageName', key: 'packageName'},
    ];

    const mainExpandRender = (record) => {
        const groups = (allGroups || []).filter(g => g.groupIdx === record.seq);
        return (
            <Table
                columns={groupColumns}
                dataSource={groups.map((g, i) => ({...g, key: g.seq || i}))}
                pagination={false}
                size="small"
                expandable={{expandedRowRender: groupExpandRender}}
                style={{marginLeft: 24}}
            />
        );
    };

    const handleCopyLiveData = async () => {
        if (currentUser.target === ENV.PRODUCTION) {
            toast.error('운영 환경에서는 라이브 데이터 복사 기능을 사용할 수 없습니다.');
            return;
        }
        const result = await internalApi.put('/store_manager/personalPackage');
        if (result) {
            toast.success('라이브 데이터 복사가 완료되었습니다.');
            if (refreshRef.current) {
                refreshRef.current(REFRESH_TYPE_FUNCTION.GAME_DATA);
            }
        }
    }

    const showCopyConfirm = () => {
        Modal.confirm({
            title: 'Live 데이터 복사',
            content: '정말로 Live 데이터를 복사하시겠습니까?',
            okText: '확인',
            cancelText: '취소',
            onOk: handleCopyLiveData,
        });
    };

    const columns = [
        {
            title: '관리 번호', dataIndex: 'seq', key: 'seq',
            sorter: (a, b) => a.seq - b.seq, width: 100,
        },
        {
            title: '메모', dataIndex: 'memo', key: 'memo', width: 200,
            render: (text, record) => (
                editingMemo[record.seq] !== undefined ? (
                    <Input
                        size="small"
                        value={editingMemo[record.seq]}
                        autoFocus
                        onChange={e => setEditingMemo(prev => ({...prev, [record.seq]: e.target.value}))}
                        onBlur={() => handleMemoSave(record, editingMemo[record.seq])}
                        onPressEnter={() => handleMemoSave(record, editingMemo[record.seq])}
                        onClick={e => e.stopPropagation()}
                    />
                ) : (
                    <span
                        style={{cursor: 'text', display: 'block', minWidth: 80, minHeight: 20}}
                        onClick={e => {
                            e.stopPropagation();
                            setEditingMemo(prev => ({...prev, [record.seq]: text || ''}));
                        }}
                    >
                        {text || <span style={{color: '#bbb'}}>클릭하여 편집</span>}
                    </span>
                )
            ),
        },
        {
            title: '등록된 패키지 수', key: 'pkgCount', width: 130,
            render: (_, record) => getGroupCount(record.seq),
        },
        {title: '마지막 결제일', dataIndex: 'lastPayment', key: 'lastPayment', width: 120},
        {title: '평균 결제일', dataIndex: 'averagePayment', key: 'averagePayment', width: 110},
        {title: '결제 횟수(이상)', dataIndex: 'paymentCount', key: 'paymentCount', width: 120},
        {
            title: '누적 기준일', dataIndex: 'baseDate', key: 'baseDate', width: 120,
            render: (v) => v ? dayjs(v).format('YYYY.MM.DD') : '-',
        },
        {title: '누적 로그인 일수(이상)', dataIndex: 'cumulativeLoginCount', key: 'cumulativeLoginCount', width: 160},
        {
            title: '누적 결제 금액(달러)', key: 'cumulativePayment', width: 160,
            render: (_, r) => (
                <Tag color={r.isCumPaymentOver ? 'blue' : 'orange'}>
                    {r.cumulativePayment} {r.isCumPaymentOver ? '▲' : '▼'}
                </Tag>
            ),
        },
        {
            title: '액션', key: 'action', width: 100,
            render: (_, record) => (
                <Button
                    size="small"
                    type="primary"
                    onClick={e => {e.stopPropagation(); handleOpenRegister(record);}}
                >
                    추가하기
                </Button>
            ),
        },
    ];

    const isLoadingAll = isLoading || isGroupLoading || isShopLoading || isShopGroupLoading || isTextLoading;
    const firstError = error || groupError || shopError || shopGroupError || textError;

    if (isLoadingAll) return <IsLoading />;
    if (firstError) return <ErrorPage error={firstError} />;

    return (
        <div>
            <div style={{marginBottom: 16}}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenEdit(null)}>
                    신규 추가
                </Button>
                {currentUser.target !== ENV.PRODUCTION && (
                    <Button onClick={showCopyConfirm} style={{marginLeft: 8}}>
                        Live 데이터 복사하기
                    </Button>
                )}
            </div>

            <Table
                columns={columns}
                dataSource={(personalPackages || []).map((r, i) => ({...r, key: r.seq || i}))}
                expandable={{expandedRowRender: mainExpandRender}}
                onRow={(record) => ({onClick: () => handleOpenEdit(record)})}
                rowClassName={() => 'cursor-pointer'}
                size="middle"
                scroll={{x: 'max-content'}}
            />

            <Modal
                open={editModalOpen}
                onCancel={() => setEditModalOpen(false)}
                footer={null}
                width="auto"
                centered
                title={selectedRecord?.seq ? `개인화 관리 편집 (번호: ${selectedRecord.seq})` : '신규 개인화 추가'}
                destroyOnClose
                styles={{
                    content: {
                        resize: 'both',
                        overflow: 'hidden',
                        width: 1100,
                        minWidth: 700,
                        maxWidth: '96vw',
                        minHeight: 400,
                    },
                    body: {
                        overflow: 'auto',
                        maxHeight: 'calc(90vh - 110px)',
                    },
                }}
            >
                {editModalOpen && (
                    <PersonalPackageEditModal
                        record={selectedRecord || new PersonalPackageEntity()}
                        handleClose={handleCloseModal}
                    />
                )}
            </Modal>

            <Modal
                open={registerModalOpen}
                onCancel={() => setRegisterModalOpen(false)}
                footer={null}
                width={900}
                title={`패키지 등록 (관리 번호: ${selectedRecord?.seq})`}
                destroyOnClose
            >
                {registerModalOpen && selectedRecord && (
                    <PersonalPackageRegisterModal
                        personalPackage={selectedRecord}
                        groupItems={(allGroups || []).filter(g => g.groupIdx === selectedRecord.seq)}
                        handleClose={handleCloseModal}
                    />
                )}
            </Modal>
        </div>
    );
};

export default PersonalPackage;
